/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened, and steps
 * between expanded photos via `--zoom-next` / `--zoom-previous` commands,
 * left/right arrow keys, and horizontal touch swipes.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // CommandEvent does not bubble, so listen in the capture phase for step commands on child dialogs.
    this.addEventListener('command', this.#handleStepCommand, { ...opts, capture: true });
    this.addEventListener('keydown', this.#handleKeydown, opts);
    this.addEventListener('pointerdown', this.#handlePointerDown, opts);
    this.addEventListener('pointerup', this.#handlePointerUp, opts);
  }

  #swipeStart = null;

  get #zoomDialogs() {
    return [...this.querySelectorAll('dialog')].filter((dialog) => dialog.querySelector('img[data-zoom-image]'));
  }

  #handleStepCommand = (event) => {
    if (event.command !== '--zoom-next' && event.command !== '--zoom-previous') return;
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    this.#step(dialog, event.command === '--zoom-next' ? 1 : -1);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const dialog = event.target instanceof Element ? event.target.closest('dialog[open]') : null;
    if (!dialog || !this.contains(dialog)) return;
    event.preventDefault();
    const forward = event.key === 'ArrowRight';
    const rtl = getComputedStyle(dialog).direction === 'rtl';
    this.#step(dialog, forward !== rtl ? 1 : -1);
  };

  #handlePointerDown = (event) => {
    if (event.pointerType !== 'touch') return;
    const dialog = event.target instanceof Element ? event.target.closest('dialog[open]') : null;
    this.#swipeStart = dialog ? { dialog, x: event.clientX, y: event.clientY } : null;
  };

  #handlePointerUp = (event) => {
    const start = this.#swipeStart;
    this.#swipeStart = null;
    if (!start || event.pointerType !== 'touch') return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const rtl = getComputedStyle(start.dialog).direction === 'rtl';
    this.#step(start.dialog, (dx < 0) !== rtl ? 1 : -1);
  };

  #step(dialog, direction) {
    const dialogs = this.#zoomDialogs;
    const index = dialogs.indexOf(dialog);
    if (index === -1 || dialogs.length < 2) return;

    const next = dialogs[(index + direction + dialogs.length) % dialogs.length];
    const image = next.querySelector('img[data-zoom-image]');
    if (image instanceof HTMLImageElement) image.loading = 'eager';

    dialog.close();
    next.showModal();
    const sameControl = next.querySelector(`[command="${direction > 0 ? '--zoom-next' : '--zoom-previous'}"]`);
    if (sameControl instanceof HTMLElement) sameControl.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
